/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from 'react';

import AppTable from 'components/appTable';
import Empty from 'components/empty';
import SearchBox from 'components/form/searchBox';
import PanelBoxLayout from 'components/layout/panelBox';
import Toolbar, { ToolbarRight } from 'components/toolbar';
import { useActions } from 'common/hooks/useActions';
import RowAction from 'components/rowAction';
import TicketFilterPopup from '../filterForm';
import PaginationApp from 'components/pagination';
import StatusChip from 'components/statusChip';
import Value from 'components/value';
import { formatDateTime, MISSING } from 'common/helpers/format';

import * as S from './styles';
import * as T from 'common/types';

const PenIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 20h9" />
    <path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4z" />
  </svg>
);

/* Results, not "show" — the destination is the answers people gave. */
const ResultsIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 20h18" />
    <path d="M7 20V10M12 20V4M17 20v-7" />
  </svg>
);

const SurveyIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round">
    <path d="M8 4h8a2 2 0 0 1 2 2v14l-6-3-6 3V6a2 2 0 0 1 2-2z" />
  </svg>
);

interface IProps {
  data: { list: []; totalPages: number };
  loading: boolean;
}

export default function SurveyTable({ data, loading }: IProps) {
  const { surveysRequestAction } = useActions();
  const [meta, setMeta] = useState({
    page: 1,
    page_size: 10,
    search: '',
    type: ''
  });

  const fetchData = () => {
    surveysRequestAction(meta);
  };

  useEffect(() => {
    fetchData();
  }, [meta]);

  const handleChangePage = (page: number) => {
    setMeta({ ...meta, page });
  };

  const handleChangePageSize = (page_size: number) => {
    setMeta({ ...meta, page_size });
  };

  return (
    <S.Content>
      <PanelBoxLayout className="flush" contentStyle={{ padding: 0 }}>
        <Toolbar>
          <SearchBox
            onChange={(e) => setMeta({ ...meta, search: e, page: 1 })}
            placeholder="Search surveys"
            value={meta.search}
          />
          <ToolbarRight>
            <TicketFilterPopup setFilter={setMeta} filter={meta} />
          </ToolbarRight>
        </Toolbar>
        <AppTable
          headers={[
            { id: 1, key: 'id', title: 'ID', dir: 'left', width: 72 },
            { id: 2, key: 'title', title: 'Title', dir: 'left' },
            { id: 3, key: 'startsAt', title: 'Start Date', dir: 'left', width: 156 },
            { id: 4, key: 'endsAt', title: 'End Date', dir: 'left', width: 156 },
            { id: 5, key: 'attendance', title: 'Responses', type: 'num', width: 110 },
            { id: 6, key: 'status', title: 'Status', dir: 'left', width: 130 },
            /* Two actions in one column, as everywhere else — Edit was its
               own headed column and Show was a bordered button repeated down
               every row, together taking 174px for two affordances. */
            { id: 7, key: 'edit', title: '', type: 'actions', width: 96 }
          ]}
          loading={loading}
          data={data.list}
          empty={
            <Empty
              icon={<SurveyIcon />}
              title={meta.search ? `No surveys match “${meta.search}”` : 'No surveys yet'}
              text={
                meta.search
                  ? 'Try a different term, or clear the filters to see every survey.'
                  : 'Create one to start collecting answers from employees.'
              }
            />
          }
          altanfeethiFields={{
            id: (item: T.Pages.SurveyItem) => (
              <S.Div className="text-left">
                <Value mono>{item?.id}</Value>
              </S.Div>
            ),

            title: (item: T.Pages.SurveyItem) => (
              <S.Div className="text-left">
                <Value fallback={MISSING.provided}>{item?.title}</Value>
              </S.Div>
            ),
            startsAt: (item: T.Pages.SurveyItem) => (
              <S.Div className="text-left">
                <Value mono>{formatDateTime(item?.startsAt)}</Value>
              </S.Div>
            ),
            /* A count of nobody is quieter than a count of somebody. */
            attendance: (item: T.Pages.SurveyItem) => (
              <S.Div className={item?.attendance ? '' : 'zero'}>{item?.attendance ?? 0}</S.Div>
            ),
            endsAt: (item: T.Pages.SurveyItem) => (
              <S.Div className="text-left">
                <Value mono>{formatDateTime(item?.endsAt)}</Value>
              </S.Div>
            ),
            status: (item: T.Pages.SurveyItem) => (
              <S.Div className="text-left">
                <StatusChip domain="survey" value={item?.status} fallback={<Value />} />
              </S.Div>
            ),
            edit: (item: T.Pages.SurveyItem) => (
              <>
                <RowAction to={`/panel/survey/${item.id}`} label="Edit survey">
                  <PenIcon />
                </RowAction>
                <RowAction
                  to={`/panel/survey/show/${item.id}`}
                  disabled={!item?.attendance}
                  label={item?.attendance ? 'See the results' : 'No responses to show yet'}
                >
                  <ResultsIcon />
                </RowAction>
              </>
            )
          }}
        />
      </PanelBoxLayout>
      {data.totalPages ? (
        <PaginationApp
          page={meta.page}
          count={meta.page_size * data.totalPages}
          handleChangePage={(page: number) => handleChangePage(page)}
          key={data.totalPages}
          pageSize={meta.page_size}
          handleChangeTake={handleChangePageSize}
        />
      ) : (
        <></>
      )}
    </S.Content>
  );
}
